import { useContext } from "react";
import { CreateCSSProperties, FanboxStyling } from "./CSSUtilities";
import { StateManager } from "./PartyState";
import { StateManagerContext } from "./StateManagerContext";
import React from "react";
import { UIConfigContext } from "./UIConfigContext";
import { GoogleLogin } from "@react-oauth/google";
import { GoogleLoginHandler } from "../User/CommonAuth";
import { MenuOverlayName } from "./Party";


export type ActionHandler = (action: Map<string, string>) => boolean;

export type ActionData = () => Map<string, string>;

export const IdActionData: (id: string) => ActionData = (id: string) => { return () => { return new Map<string, string>([['id', id]]); } }
export const EmptyActionData: ActionData = () => { return new Map<string, string>() };

export type ActionPermissionType = 'signedIn' | 'boosts';
export type ActionPermission = {
    permission: ActionPermissionType;
    threshold?: number;
    message?: string;
}

export const CheckPermission = (permission: ActionPermission, stateManager: StateManager): boolean => {
    if (permission.permission === 'signedIn') {
        return stateManager.user().userInformation.signUpComplete;
    }
    else if (permission.permission === 'boosts') {
        const threshold = permission.threshold ?? 1000;
        return stateManager.user().accountInformation.boostBalance >= threshold;
    }
    else
        return false;
}

export type Action = {
    name: () => string;
    handle: ActionHandler;
    addHandler: (handler: ActionHandler) => number;
    removeHandler: (id: number) => boolean;
    permissions: ActionPermission[];
    useSignInOverlay? : boolean;
}

export const CreateAction = (name: string, permissions: ActionPermission[], handler?: ActionHandler): Action => {

    const handlers = new Map<Number, ActionHandler>();

    const ret: Action = {
        name: () => name,
        handle: (action: Map<string, string>): boolean => {
            var stop = false;
            handlers.forEach((handler) => {
                if (!stop)
                    stop = stop || handler(action);
            });
            return stop;
        },
        addHandler: (handler: ActionHandler): number => {
            const id = Math.random();
            handlers.set(id, handler);
            return id;
        },
        removeHandler: (id: number): boolean => {
            if (handlers.has(id)) {
                handlers.delete(id);
                return true;
            }
            else {
                return false;
            }
        },
        permissions: permissions
    }
    if (handler !== undefined)
        ret.addHandler(handler);
    return ret;
}

export type ActionPromptPosition = 'left' | 'center' | 'right';

export type ActionButtonProps = {
    styling: FanboxStyling;
    action: Action;
    actionData: ActionData;
    text: string;
    prompt: ActionPromptPosition;
}

type ActionButtonState = 'clickable' | 'signIn' | 'purchase';

export const ActionButton = ({ styling, text, action, actionData, prompt }: ActionButtonProps): JSX.Element => {
    const [buttonState, setButtonState] = React.useState<ActionButtonState>('clickable');
    const messageRef = React.useRef<string>('');


    const stateManager = useContext(StateManagerContext);
    const uiConfig = useContext(UIConfigContext);


    var onClick = () => {
        var pIndex = -1;
        for (let i = 0; i < action.permissions.length; i++) {
            const check = CheckPermission(action.permissions[i], stateManager);
            if (!check) {
                pIndex = i;
                i = action.permissions.length;
            }
        }

        if (pIndex === -1) {
            action.handle(actionData());
        }
        else {
            const permission = action.permissions[pIndex];
            if (permission.permission === 'signedIn') {
                if(action.useSignInOverlay === true) {
                    uiConfig.showEventOverlay('signIn', {});
                }
                else {
                const message = permission.message ?? 'Please sign into Google to continue';
                messageRef.current = message;
                setButtonState('signIn');
                }
            }
            else if (permission.permission === 'boosts') {
                const message = permission.message ?? 'Please purchase boosts to access this experience';
                messageRef.current = message;
                setButtonState('purchase');
            }
        }
    };


    const cssProperties = CreateCSSProperties(styling);
    if (styling.boundary?.radius === undefined)
        cssProperties.borderRadius = '10px';
    cssProperties.display = 'flex';
    cssProperties.flexDirection = 'column';
    cssProperties.alignItems = 'center';
    cssProperties.justifyContent = 'center';
    cssProperties.position = 'relative';

    const innerStyling: FanboxStyling = {
        width: uiConfig.width * 0.5,
        color: {
            foreground: 'black',
            background: 'white'
        },
        boundary: {
            border: {
                quadType: 'border',
                top: 2,
                bottom: 2,
                left: 2,
                right: 2,
                borderStyle: 'solid',
                color: 'pink',
            }
        },
        font: {
            size: 18,
            weight: 'semiBold'
        },
        position: {
            positioningType: 'absolute',
            top: 0,
        }
    }

    if (prompt === 'right' && innerStyling.position !== undefined)
        innerStyling.position.left = 0;
    else if (prompt === 'left' && innerStyling.position !== undefined)
        innerStyling.position.right = 0;
    else if (innerStyling.position !== undefined)
        innerStyling.position.left = -(0.5 * uiConfig.width - (styling.width ?? 100))



    return (<div style={cssProperties}
        onClick={() => { onClick(); }}>

        {(buttonState === 'clickable') && <span onClick={() => { onClick(); }}>{text}</span>}
        {(buttonState === 'signIn') && (() => {

            const css = CreateCSSProperties(innerStyling);

            return <div style={css}>
                <span>{messageRef.current}</span>
                <GoogleLogin onSuccess={(credentials) => {
                    GoogleLoginHandler(credentials, stateManager);
                    setButtonState('clickable');
                }} />
            </div>

        })()}
        {(buttonState === 'purchase') && (() => {

            const css = CreateCSSProperties(innerStyling);
            const handler: ActionHandler = (action) => {
                const id = (action.get('id') ?? 'home') as MenuOverlayName;
                uiConfig.showMenuItem(id);
                return true;
            }

            const action = CreateAction('openPurchase', [], handler);
            const buttonStyle: FanboxStyling = {
                width: styling.width,
                height: styling.height,
                color: {
                    foreground: 'white',
                    background: 'darkPink'
                },
                boundary: {
                    border: {
                        quadType: 'border',
                        top: 2,
                        bottom: 2,
                        left: 2,
                        right: 2,
                        color: 'white',
                        borderStyle: 'solid'
                    }
                },
                font: {
                    size: 20,
                    weight: 'bold'
                }
            }
            const props: ActionButtonProps = {
                action: action,
                styling: buttonStyle,
                actionData: IdActionData('purchase'),
                text: 'Purchase',
                prompt: prompt

            }

            return <div style={css}>
                <span>{messageRef.current}</span>
                <ActionButton {...props} />
            </div>

        })()}
    </div>)

}